import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardMedia from '@mui/material/CardMedia';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import get from 'lodash/get';
import { Labeled, useInput } from 'react-admin';

import CopyURLButton from './copy-video-url-button';
import VideoChooserButton from './video_chooser_button';
import VideoUploaderButton from './video_uploader_button';
import RemoveButton from './remove-video-button';

const StyledThumbnailContainer = styled('div')({
    position: 'relative',
});

const StyledActions = styled(CardActions)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
    paddingBottom: typeof theme?.spacing === 'function' ? theme.spacing(1) : '8px',
}));

const VideoInput = ({
    fullWidth = true,
    helperText,
    label = 'Upload Video',
    ...props
}) => {
    const { field, fieldState, isRequired } = useInput(props);

    const handleChooserChoice = useCallback((video) => {
        field.onChange(video);
    }, [field]);

    const handleVideoUploaded = useCallback((video) => {
        field.onChange(video);
    }, [field]);

    const handleVideoRemoved = useCallback(() => {
        field.onChange(null);
        field.onBlur();
    }, [field]);

    return (
        <Labeled
            fullWidth={fullWidth}
            label={label}
            isRequired={isRequired}
            meta={fieldState}
            input={field}
            resource={props.resource}
            source={props.source}
        >
            <Grid container sx={{ width: '100%' }} spacing={0}>
                <Grid size={12}>
                    <Card variant="outlined">
                        <Grid container sx={{ width: '100%' }} spacing={0}>
                            {field.value ?
                                <Grid size={{
                                    xs: 12,
                                    sm: 3,
                                    md: 4,
                                }}
                                >
                                    <StyledThumbnailContainer>
                                        <CardMedia
                                            component="video"
                                            src={get(field.value, 'source')}
                                        />
                                    </StyledThumbnailContainer>
                                </Grid> :
                                null}
                            <Grid size={{
                                xs: 12,
                                sm: 9,
                                md: 8,
                            }}
                            >
                                <StyledActions>
                                    <VideoChooserButton folderName="featured" onVideoChoosed={handleChooserChoice} />
                                    <VideoUploaderButton folderName="featured" onVideoUploaded={handleVideoUploaded} />
                                </StyledActions>
                                {field.value ?
                                    <StyledActions>
                                        <CopyURLButton video={field.value} />
                                        <RemoveButton
                                            video={field.value}
                                            onRemove={handleVideoRemoved}
                                        />
                                    </StyledActions> :
                                    null}
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
                { fieldState.touched && fieldState.error ? <Grid size={12}><FormHelperText>{fieldState.error}</FormHelperText></Grid> : null }
                { helperText ? <Grid size={12}><FormHelperText>{helperText}</FormHelperText></Grid> : null }
            </Grid>
        </Labeled>
    );
};

VideoInput.propTypes = {
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    isRequired: PropTypes.bool,
    label: PropTypes.string,
    resource: PropTypes.string,
    source: PropTypes.string,
    required: PropTypes.bool,
};

export default React.memo(VideoInput);
